import React from 'react';
import { useTheme } from '../ThemeProvider';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Sun, Moon, Monitor } from 'lucide-react';

interface ThemeToggleProps {
  variant?: 'button' | 'dropdown';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export function ThemeToggle({ variant = 'button', size = 'sm', className = '' }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  const toggleTheme = () => {
    // Alterna apenas entre dark e light (sem passar por system)
    const effectiveTheme = getEffectiveTheme();
    setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
  };

  const getThemeIcon = (themeName: string, isActive: boolean = false) => {
    const iconClass = `h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`;

    switch (themeName) {
      case 'light':
        return <Sun className={iconClass} />;
      case 'dark':
        return <Moon className={iconClass} />;
      case 'system':
        return <Monitor className={iconClass} />;
      default:
        return <Monitor className={iconClass} />;
    }
  };

  const getThemeLabel = (themeName: string) => {
    switch (themeName) {
      case 'light':
        return 'Claro';
      case 'dark':
        return 'Escuro';
      case 'system':
        return 'Sistema';
      default:
        return 'Sistema';
    }
  };

  const getCurrentIcon = () => {
    const effectiveTheme = getEffectiveTheme();
    const isDark = effectiveTheme === 'dark';

    if (theme === 'system') {
      return (
        <div className='relative'>
          <Monitor className='h-5 w-5 text-foreground group-hover:text-accent transition-colors duration-200' />
          <div className='absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-500' />
        </div>
      );
    }

    return isDark ? (
      <Moon className='h-5 w-5 text-blue-400 group-hover:text-blue-300 transition-colors duration-200' />
    ) : (
      <Sun className='h-5 w-5 text-amber-500 group-hover:text-amber-400 transition-colors duration-200' />
    );
  };

  if (variant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size={size}
            className={`w-10 h-10 p-0 relative group bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 backdrop-blur-sm border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 rounded-lg transition-all ${className}`}
            title='Alterar tema'
          >
            {getCurrentIcon()}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='bg-white/98 dark:bg-gray-900/98 backdrop-blur-lg border border-gray-200 dark:border-gray-700 shadow-xl'
        >
          <DropdownMenuItem
            onClick={() => setTheme('light')}
            className='hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-900 dark:text-gray-100 cursor-pointer'
          >
            <Sun className='h-4 w-4 mr-2 text-amber-500' />
            <span className='font-medium'>Tema Claro</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setTheme('dark')}
            className='hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-900 dark:text-gray-100 cursor-pointer'
          >
            <Moon className='h-4 w-4 mr-2 text-blue-400' />
            <span className='font-medium'>Tema Escuro</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setTheme('system')}
            className='hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-900 dark:text-gray-100 cursor-pointer'
          >
            <Monitor className='h-4 w-4 mr-2 text-purple-500' />
            <span className='font-medium'>Sistema</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default button variant
  return (
    <Button
      variant='ghost'
      size={size}
      onClick={toggleTheme}
      className={`w-10 h-10 p-0 relative group bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 backdrop-blur-sm border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 rounded-lg transition-all ${className}`}
      title={`Tema atual: ${getThemeLabel(theme)} - Clique para alternar`}
    >
      <div className='relative'>
        {getCurrentIcon()}
        <div className='absolute inset-0 rounded-full bg-current opacity-0 group-hover:opacity-10 transition-opacity duration-200' />
      </div>
    </Button>
  );
}

// Componente compacto para uso em barras laterais
export function CompactThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  const themes = [
    { value: 'light', label: 'Claro', icon: Sun },
    { value: 'dark', label: 'Escuro', icon: Moon },
    { value: 'system', label: 'Sistema', icon: Monitor },
  ];

  return (
    <div className={`flex items-center gap-1 p-1 bg-muted rounded-lg ${className}`}>
      {themes.map(({ value, label, icon: Icon }) => (
        <Button
          key={value}
          variant={theme === value ? 'default' : 'ghost'}
          size='xs'
          onClick={() => setTheme(value as any)}
          className={`h-8 w-8 p-0 ${
            theme === value ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/50'
          }`}
          title={label}
        >
          <Icon className='h-3.5 w-3.5' />
        </Button>
      ))}
    </div>
  );
}
